// src/services/appearance.js
//
// 🟢 PER-CHAT THEME — web only, deliberately unsynced (localStorage, never Firestore).
//
// 🟢 THE SAME EIGHT THEMES AS THE PHONE. The list is ./chatThemes.js — generated
//    from the app's services/chatThemes.ts — so a theme here is the theme there:
//    the same wallpaper, the same doodle, the same bubble, in light and in dark.
//    A setting is: which theme, whether the doodle is on, the text size — and, in
//    place of a theme's wallpaper, your own photo.
//
// 🟢 CLASSES, NOT INLINE STYLES. Every theme has a light and a dark face and only a
//    stylesheet can answer `prefers-color-scheme`, so each theme is a class whose
//    rules are written ONCE, from the shared list, into a <style> on first use.
//
// 🟢 THE DOODLE IS THE APP'S TILE, TINTED HERE. CSS cannot tint a background
//    image, so on first use a canvas paints the white tile in the theme's ink —
//    exactly what the app's `tintColor` does — and the result becomes that theme's
//    `--wp-doodle`. One asset, eight inks, made only for themes someone opens.
//
// Storage: `allo_ap_<chatId>` per chat, `allo_ap_default` for "apply to all"; a
// photo is kept apart (`allo_ap_photo_<chatId>`), resized to a screen's worth,
// so the small settings object stays small.
// Resolution: chat override → default → built-in. Settings saved by the older
// panel (theme ids like 'emerald', `pattern` for the doodle) are read through.

import { CHAT_THEMES, DEFAULT_THEME, themeById } from './chatThemes';
import { getAdaptiveTickColors } from './tickTone';   // 🔵 the ticks measured against this theme's bubble
import doodleTile from '../assets/doodle@2x.png';

export const DEFAULT_AP = { theme: DEFAULT_THEME, doodle: true, size: 'normal', photo: false };
/* the same four sizes as the phone (src/utils/textSize.ts) */
export const TEXT_SIZES = [
  { id: 'small', name: 'Small' },
  { id: 'normal', name: 'Default' },
  { id: 'large', name: 'Large' },
  { id: 'x-large', name: 'Larger' },
];
export const THEMES = CHAT_THEMES;

const keyOf = (chatId) => `allo_ap_${chatId}`;
const DEFAULT_KEY = 'allo_ap_default';
const photoKeyOf = (chatId) => `allo_ap_photo_${chatId}`;
const DEFAULT_PHOTO_KEY = 'allo_ap_photo_default';
function readRaw(key) { try { return localStorage.getItem(key); } catch { return null; } }

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** An older saved setting, read in today's shape. */
function normalise(s) {
  if (!s) return null;
  const out = {};
  if (s.theme) out.theme = (themeById(s.theme) || themeById(DEFAULT_THEME)).id;
  if (typeof s.doodle === 'boolean') out.doodle = s.doodle;
  else if (typeof s.pattern === 'boolean') out.doodle = s.pattern;
  if (TEXT_SIZES.some((x) => x.id === s.size)) out.size = s.size;
  else if (s.size === 'medium') out.size = 'large';          // the old panel's middle step
  if (typeof s.photo === 'boolean') out.photo = s.photo;
  return out;
}

/** chat override → account default → built-in */
export function loadAppearance(chatId) {
  const def = normalise(read(DEFAULT_KEY)) || {};
  const own = normalise(read(keyOf(chatId)));
  const s = { ...DEFAULT_AP, ...def, ...(own || {}) };
  /* this chat's own photo; or, for a chat with no setting of its own, the one "apply to all" chose */
  s.photoUrl = readRaw(photoKeyOf(chatId)) || (!own && def.photo ? readRaw(DEFAULT_PHOTO_KEY) : null) || null;
  if (!s.photoUrl) s.photo = false;
  return s;
}

const pick = (s) => ({ theme: s.theme, doodle: !!s.doodle, size: s.size || 'normal', photo: !!(s.photo && s.photoUrl) });

/** Saves the setting, and the photo when it changed. Returns false if the browser's storage was full. */
export function saveAppearance(chatId, settings) {
  try {
    localStorage.setItem(keyOf(chatId), JSON.stringify(pick(settings)));
    if (settings.photoUrl && readRaw(photoKeyOf(chatId)) !== settings.photoUrl) localStorage.setItem(photoKeyOf(chatId), settings.photoUrl);
    return true;
  } catch { return false; }
}

/** A picture from the computer → a JPEG a screen's worth wide, as a data URL (what localStorage can hold). */
export function photoToDataUrl(file, max = 1440) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** "Apply to all chats": becomes the default AND clears this chat's override so the
 *  two can't drift apart one second after the button said "all". */
export function applyToAllChats(chatId, settings) {
  try {
    localStorage.setItem(DEFAULT_KEY, JSON.stringify(pick(settings)));
    if (settings.photo && settings.photoUrl) localStorage.setItem(DEFAULT_PHOTO_KEY, settings.photoUrl);
    localStorage.removeItem(keyOf(chatId));
  } catch { /* full */ }
}

export function resetAppearance(chatId) {
  try { localStorage.removeItem(keyOf(chatId)); localStorage.removeItem(photoKeyOf(chatId)); } catch { /* ignore */ }
  return loadAppearance(chatId);
}

/* ── the stylesheet, written from the shared list ───────────────────────── */
let _sheet = null;
const faceVars = (f, light) => [
  `--wp-color:${f.bg}`,
  `--bubble-out:${f.out}`,
  '--bubble-out-ink:#ffffff',
  /* the read tick on this bubble: a blue at 3:1 or better, plainly not the delivered tick (services/tickTone) */
  `--tick-out-read:${getAdaptiveTickColors(f.out).read}`,
  `--tick-out-muted:${getAdaptiveTickColors(f.out).muted}`,
  `--ap-accent:${f.out}`,
  /* links and the send button follow the theme in light mode; at night the app's
     own lighter blue stays, because a deep bubble colour is too dark to read as a link */
  ...(light ? [`--allo-blue:${f.out}`] : []),
].join(';');
function sheet() {
  if (_sheet || typeof document === 'undefined') return _sheet;
  _sheet = document.createElement('style');
  _sheet.id = 'allo-chat-themes';
  _sheet.textContent = CHAT_THEMES.map((t) => `.ap-t-${t.id}{${faceVars(t.light, true)}}`).join('\n')
    + '\n@media (prefers-color-scheme: dark){' + CHAT_THEMES.map((t) => `.ap-t-${t.id}{${faceVars(t.dark, false)}}`).join('') + '}';
  document.head.appendChild(_sheet);
  return _sheet;
}

/* ── the doodle, tinted per theme ──────────────────────────────────────── */
const _inked = new Set();
let _tile = null;
function tile() {
  if (_tile) return _tile;
  _tile = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = doodleTile;
  });
  return _tile;
}
function inkUrl(img, ink, op) {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const g = c.getContext('2d');
  g.globalAlpha = op;
  g.drawImage(img, 0, 0);
  g.globalAlpha = 1;
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = ink;
  g.fillRect(0, 0, c.width, c.height);
  return c.toDataURL('image/png');
}
function inkDoodle(id) {
  if (_inked.has(id) || typeof document === 'undefined') return;
  _inked.add(id);
  const t = themeById(id);
  if (!t) return;
  tile().then((img) => {
    const s = sheet();
    if (!s) return;
    s.textContent += `\n.ap-pat.ap-t-${t.id}{--wp-doodle:url(${inkUrl(img, t.light.ink, t.light.op)})}`
      + `\n@media (prefers-color-scheme: dark){.ap-pat.ap-t-${t.id}{--wp-doodle:url(${inkUrl(img, t.dark.ink, t.dark.op)})}}`;
  }).catch(() => { _inked.delete(id); });
}

/** The class string the pane (and the preview) wears. */
export function apClasses(s) {
  if (!s) return '';
  sheet();
  const t = themeById(s.theme) || themeById(DEFAULT_THEME);
  const out = [`ap-t-${t.id}`];
  if (s.photo && s.photoUrl) out.push('ap-photo');
  else if (s.doodle !== false) { out.push('ap-pat'); inkDoodle(t.id); }
  if (s.size && s.size !== 'normal') out.push(`ap-s-${s.size}`);
  return out.join(' ');
}

/** The one thing a class cannot carry: this chat's own photo. Goes on the same element as apClasses. */
export function apStyle(s) {
  return s && s.photo && s.photoUrl ? { '--wp-photo': `url("${s.photoUrl}")` } : undefined;
}
